import React, { useEffect, useState } from 'react';

const API_BASE = 'http://localhost:5001';

import AdminProductForm from './AdminProductForm';
import './AdminProducts.css';

const PAGE_SIZE = 10;

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('http://localhost:5001/api/products');
      if (!res.ok) {
        throw new Error('Erreur lors du chargement des produits');
      }
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error(err);
      setError('Impossible de charger les produits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleProductAdded = () => {
    setEditingProduct(null);
    fetchProducts();
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (productId) => {
    const confirmation = window.confirm('Supprimer ce produit ?');
    if (!confirmation) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`http://localhost:5001/api/products/${productId}`, {
        method: 'DELETE',
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        }
      });
      if (!res.ok) {
        throw new Error('Échec de la suppression');
      }
      if (editingProduct && editingProduct._id === productId) {
        setEditingProduct(null);
      }
      fetchProducts();
    } catch (err) {
      console.error(err);
      setError('Impossible de supprimer ce produit');
    }
  };

  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const displayedProducts = products.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [products.length, currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  return (
    <div className="admin-products">
      <div className="admin-products__brand">Global Sourcing Solution Group</div>
      <div className="admin-products__card">
        <div className="admin-products__intro">
          <h3>Gestion des produits</h3>
          <p>
            Enrichissez votre cave professionnelle en ajoutant de nouvelles références,
            mettez à jour les fiches existantes et assurez un suivi précis des stocks.
          </p>
          <ul>
            <li>Ajoutez vos crus avec toutes les informations essentielles</li>
            <li>Illustrez chaque produit grâce à un visuel attractif</li>
            <li>Gardez le contrôle sur les prix et la disponibilité</li>
          </ul>
        </div>
        <div className="admin-products__form">
          <h4>{editingProduct ? 'Modifier un produit' : 'Ajouter un produit'}</h4>
          <p className="admin-products__subtitle">
            {editingProduct ? 'Mettez à jour les informations de la référence sélectionnée.' : 'Complétez les informations ci-dessous pour publier une nouvelle référence dans Global Sourcing Solution Group.'}
          </p>
          <AdminProductForm
            onProductAdded={handleProductAdded}
            editingProduct={editingProduct}
            onEditCancel={() => setEditingProduct(null)}
          />
        </div>
      </div>
      <div className="admin-products__list">
        <h4>Produits ajoutés</h4>
        {loading ? (
          <p>Chargement des produits...</p>
        ) : error ? (
          <p className="admin-products__error">{error}</p>
        ) : products.length === 0 ? (
          <p>Aucun produit ajouté pour le moment.</p>
        ) : (
          <table className="admin-products__table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Prix</th>
                <th>Quantité</th>
                <th>Visuel</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedProducts.map((product) => (
                <tr key={product._id}>
                  <td>{product.name}</td>
                  <td>{product.category || '—'}</td>
                  <td>{product.price ? `${product.price} €` : '—'}</td>
                  <td>{product.quantity ?? '—'}</td>
                  <td>
                    {product.image ? (
                      <img src={`${API_BASE}${product.image}`} alt={product.name} className="admin-products__thumbnail" />
                    ) : (
                      <span>—</span>
                    )}
                  </td>
                  <td>
                    <div className="admin-products__actions">
                      <button type="button" className="admin-products__action-btn" onClick={() => handleEdit(product)}>Modifier</button>
                      <button type="button" className="admin-products__action-btn admin-products__action-btn--danger" onClick={() => handleDelete(product._id)}>Supprimer</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && !error && products.length > PAGE_SIZE && (
          <div className="admin-products__pagination">
            <button
              type="button"
              className="admin-products__pagination-btn"
              onClick={() => handlePageChange(safePage - 1)}
              disabled={safePage === 1}
            >
              Précédent
            </button>
            <div className="admin-products__pagination-pages">
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`admin-products__pagination-btn ${page === safePage ? 'is-active' : ''}`}
                  onClick={() => handlePageChange(page)}
                  aria-current={page === safePage ? 'page' : undefined}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="admin-products__pagination-btn"
              onClick={() => handlePageChange(safePage + 1)}
              disabled={safePage === totalPages}
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminProducts;
